import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { roleApi, permissionApi } from '@/services/permissionsRolesApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { LoadingState, ErrorState } from '@/components/common';
import type { Role, RoleRequest, RoleUpdateRequest, Permission } from '@/types/permissionsRoles';

// Zod schema
const roleSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(50, 'Name must be at most 50 characters'),
  description: z.string().max(500, 'Description must be at most 500 characters').optional().or(z.literal('')),
  permissionIds: z.array(z.string()).optional(),
  active: z.boolean().optional(),
});

type RoleFormData = z.infer<typeof roleSchema>;

export const RoleFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [permissionsError, setPermissionsError] = useState<unknown | null>(null);
  const { hasRole } = useAdminAuth();

  const canAccess = hasRole('ADMIN') || hasRole('SYSTEM');

  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: '',
      description: '',
      permissionIds: [],
      active: true,
    },
  });

  const watchedName = form.watch('name');

  // Memoize the fetch function to prevent useEntity from recreating on every render
  const fetchRoleById = useCallback((roleId: string) => roleApi.getById(roleId), []);

  // Fetch role data for edit mode
  const {
    data: _role,
    isLoading: isLoadingRole,
    error: roleError,
  } = useEntity<Role>({
    fetchFn: fetchRoleById,
    entityId: id,
    entityName: 'role',
    enabled: isEdit && !!id && canAccess,
    onSuccess: (data) => {
      // API returns permissions array (full Permission objects), but form uses permissionIds (string array)
      const permissionIds = data.permissions?.map(p => p.id) || [];
      
      console.log('[RoleFormPage] Role loaded:', {
        roleId: data.id,
        roleName: data.name,
        permissionsCount: data.permissions?.length || 0,
        permissionIds,
      });
      
      // Reset form with role data, including existing permissions
      form.reset({
        name: data.name,
        description: data.description || '',
        permissionIds: permissionIds,
        active: data.active,
      });
    },
    onError: () => {
      navigate('/admin/roles');
    },
  });

  const fetchAvailablePermissionsRef = useRef(false);

  // Memoize fetch function to prevent dependency issues
  const fetchAvailablePermissions = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchAvailablePermissionsRef.current) {
      return;
    }

    fetchAvailablePermissionsRef.current = true;
    setIsLoadingPermissions(true);
    setPermissionsError(null);

    try {
      console.log('[RoleFormPage] Fetching available permissions...');
      // Fetch all active permissions with a large page size to get all of them
      const response = await permissionApi.getActive({ 
        page: 0, 
        size: 1000,
        sort: 'name,asc' // Sort by name for better UX
      });
      
      console.log('[RoleFormPage] Fetched permissions:', {
        count: response.content?.length || 0,
        totalElements: response.page?.totalElements || 0,
      });
      
      // Sort permissions by name for better UX
      const sortedPermissions = (response.content || []).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      
      setAvailablePermissions(sortedPermissions);
      setPermissionsError(null);
    } catch (error: unknown) {
      const apiError = error as { message?: string; status?: number };
      console.error('[RoleFormPage] Error fetching permissions:', apiError);
      setPermissionsError(error);
      
      // Don't clear existing permissions on 429 - keep them for better UX
      if (apiError.status !== 429) {
        setAvailablePermissions([]);
      }
    } finally {
      fetchAvailablePermissionsRef.current = false;
      setIsLoadingPermissions(false);
    }
  }, []);

  // Fetch available permissions when component mounts or when canAccess changes
  useEffect(() => {
    if (!canAccess) {
      return;
    }

    fetchAvailablePermissions();
  }, [canAccess, fetchAvailablePermissions]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: RoleFormData) => {
      const payload: RoleRequest = {
        name: data.name,
        description: data.description || undefined,
        permissionIds: data.permissionIds || [],
        active: data.active ?? true,
      };
      return roleApi.create(payload);
    },
    entityName: 'role',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/roles');
    },
  });

  // Update mutation
  // Note: Name cannot be updated - only description, permissionIds, and active can be changed
  const updateMutation = useMutation({
    mutationFn: async (data: RoleFormData) => {
      if (!id) throw new Error('Role ID is required');
      // For updates, only send fields that can be changed (name is NOT included)
      const payload: RoleUpdateRequest = {
        description: data.description || undefined,
        permissionIds: data.permissionIds || [],
        active: data.active ?? true,
      };
      return roleApi.update(id, payload);
    },
    entityName: 'role',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/roles');
    },
  });

  const onSubmit = async (data: RoleFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  if (!canAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to manage roles.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/roles">Back to List</Link>
        </Button>
      </div>
    );
  }

  if (isEdit && isLoadingRole) {
    return <LoadingState message="Loading role..." />;
  }

  if (isEdit && roleError) {
    return (
      <div className="space-y-4">
        <ErrorState error={roleError} />
        <div className="text-center">
          <Button asChild>
            <Link to="/admin/roles">Back to List</Link>
          </Button>
        </div>
      </div>
    );
  }

  const selectedPermissionIds = form.watch('permissionIds') || [];
  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/roles">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Role' : 'Create Role'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update role information' : 'Create a new role'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Role Information</CardTitle>
              <CardDescription>Enter role details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEdit && (
                <div className="flex items-start gap-3 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Role name cannot be changed
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      Once a role is created, its name cannot be modified. You can update the description, permissions, and active status.
                    </p>
                  </div>
                </div>
              )}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter role name"
                        {...field}
                        disabled={isEdit}
                      />
                    </FormControl>
                    <FormDescription>
                      {isEdit ? (
                        <span className="text-muted-foreground">
                          Role name cannot be changed after creation. {watchedName.length}/50 characters.
                        </span>
                      ) : (
                        <span>{watchedName.length}/50 characters (minimum 2)</span>
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter role description"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {field.value?.length || 0}/500 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="permissionIds"
                render={() => (
                  <FormItem>
                    <FormLabel>Permissions</FormLabel>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto border rounded-md p-4 bg-muted/50">
                      {isLoadingPermissions ? (
                        <div className="flex items-center justify-center py-8">
                          <p className="text-sm text-muted-foreground">Loading permissions...</p>
                        </div>
                      ) : permissionsError ? (
                        <div className="flex flex-col items-center justify-center py-8 space-y-2">
                          <p className="text-sm text-destructive">
                            Error loading permissions. Please try refreshing the page.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              fetchAvailablePermissionsRef.current = false;
                              fetchAvailablePermissions();
                            }}
                          >
                            Retry
                          </Button>
                        </div>
                      ) : availablePermissions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No permissions available
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {availablePermissions.map((permission) => {
                            const isSelected = selectedPermissionIds.includes(permission.id);
                            return (
                              <div
                                key={permission.id}
                                className={`flex items-start gap-3 p-2 rounded-md hover:bg-background transition-colors ${
                                  isSelected ? 'bg-primary/5 border border-primary/20' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  id={`permission-${permission.id}`}
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const currentIds = form.getValues('permissionIds') || [];
                                    if (e.target.checked) {
                                      // Add permission
                                      form.setValue('permissionIds', [...currentIds, permission.id], {
                                        shouldValidate: true,
                                      });
                                    } else {
                                      // Remove permission
                                      form.setValue(
                                        'permissionIds',
                                        currentIds.filter((id) => id !== permission.id),
                                        { shouldValidate: true }
                                      );
                                    }
                                  }}
                                  className="mt-1 rounded border-gray-300 cursor-pointer"
                                />
                                <Label
                                  htmlFor={`permission-${permission.id}`}
                                  className="flex-1 cursor-pointer space-y-1"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{permission.name}</span>
                                    {permission.systemPermission && (
                                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                                        System
                                      </span>
                                    )}
                                  </div>
                                  {permission.description && (
                                    <div className="text-sm text-muted-foreground">
                                      {permission.description}
                                    </div>
                                  )}
                                  {(permission.resource || permission.action) && (
                                    <div className="text-xs text-muted-foreground flex gap-2">
                                      {permission.resource && (
                                        <span className="font-medium">Resource:</span>
                                      )}
                                      {permission.resource && <span>{permission.resource}</span>}
                                      {permission.action && permission.resource && (
                                        <span>•</span>
                                      )}
                                      {permission.action && (
                                        <span className="font-medium">Action:</span>
                                      )}
                                      {permission.action && <span>{permission.action}</span>}
                                    </div>
                                  )}
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <FormDescription>
                      {isLoadingPermissions
                        ? 'Loading permissions...'
                        : `Select permissions to assign to this role. ${selectedPermissionIds.length} of ${availablePermissions.length} permissions selected.`}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value ?? true}
                        onChange={field.onChange}
                        className="rounded border-gray-300"
                      />
                    </FormControl>
                    <FormLabel>Active</FormLabel>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/roles">Cancel</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.setValue('active', false);
                form.handleSubmit(onSubmit)();
              }}
              disabled={isLoading}
            >
              Save as Draft
            </Button>
            <Button type="submit" disabled={isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default RoleFormPage;
